import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  Modal,
  Tab,
  Tabs,
} from "@mui/material";
import { useForm } from "react-hook-form";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFTextField,
} from "../../../../../RHFComponents/RHFComponents";
import { generalResearch, medpgmgp } from "../../../../../../networking";
import { toBool01 } from "./MedintAgendaForm/MedintAgendaManagement/SospensioniAgendeModalDetails";
import { AllertMessage } from "./MedintAgendaForm/MedintAgendaFormDetail";

type Props = {
  open: boolean;
  onClose: () => void;
  selectedRow?: any;
};

// Dati che arrivano dall'api
type MedpgmgpApiRow = {
  MEDPGMGP_PRGINT?: string;
  MEDPGMP_PRGINT?: string;
  MEDPGMGP_PREN_CUP?: string;
  MEDPGMGP_ESPOS_SISS?: string;
  MEDPGMGP_PREN_SISS?: string;
  MEDPGMGP_PREN_ALTRO?: string;
  MEDPGMGP_PREN_MEDICO?: string;
  MEDPGMGP_PREN_FARM?: string;
  MEDPGMGP_PREN_CCR?: string;
  MEDPGMGP_PREN_CITT?: string;
  MEDPGMGP_PREN_CREG?: string;
  MEDPGMGP_URGENTI?: string;
  MEDPGMGP_ORDINARIE?: string;
  MEDPGMGP_AREA_RISCHIO?: string;
  MEDPGMGP_PROGRAMMABILI?: string;
  GPINV_CODICE?: string;
  MEDPGMGP_TPACC_NON_SISS?: string;
  MEDPGMGP_PROG_A?: string;
  MEDPGMGP_PROG_D?: string;
  MEDPGMGP_ORD_D?: string;
  MEDPGMGP_PID_ACU?: string;
  MEDPGMGP_TIMESTAMP?: string;
  MEDPGMGP_SW_ESCL_GP?: string;
  DECODE_GPINV_GPINV_DESCRIZIONE?: string;
};

//Tipo di dati del form
type VincoliPrenotabilitaForm = {
  programma: string;
  specialita: string;
  prestazione: string;
  tipologia: string;
  tipoInviante: string;
  esclusaDaGp: boolean;
  urgenti: boolean;
  primiAccessiPriorP: boolean;
  primiAccessiPriorD: boolean;
  primiAccessiPriorAlta: boolean;
  altriAccessiPriorP: boolean;
  altriAccessiPriorD: boolean;
  altriAccessiPriorAlta: boolean;
  farmacista: boolean;
  cittadino: boolean;
  altroOperatore: boolean;
  ccr: boolean;
  mmg: boolean;
  creg: boolean;
  espostaAlSiss: boolean;
  tipoAccessoNonSiss: string;
};

//Costante di stile per i titoli di sezione
const sectionTitleSx = {
  fontFamily: "Rubik",
  fontWeight: 500,
  fontSize: 16,
  mr: 2,
  whiteSpace: "nowrap",
  color: " var(--text-primary, rgba(0, 0, 0, 0.87))",
};

//Elenco delle opzioni disponibili per il tipo di accesso non Siss. Il value è il codice da inviare all'api
const labelNonSiss = [
  {
    value: "1",
    label: "Sportello Aziendale",
  },
  {
    value: "2",
    label: "Pren. telefoniche con operatori Aziendali",
  },
  {
    value: "3",
    label: "Pren. telefoniche con operatori CCR",
  },
  {
    value: "4",
    label: "Senza prenotazione",
  },
];

//Funzione per mappare i dati dell'api nel form.
//Se selectedRow non viene passato, restituisce i valori di default.
//E' comoda per evitare di dover chiamare reset() ogni volta che viene chiamata un'api e riscrivere i valori
const mapMedpgmgpToForm = (
  apiData?: MedpgmgpApiRow,
  selectedRow?: any,
): VincoliPrenotabilitaForm => {
  //Valori di default del form, servono nel caso dobbiamo inserire dei dati nuovi
  const defaults = getDefaultValues(selectedRow);

  //apiData contiene i dati che arrivano dall'api, se non li ho restituisco defaults
  if (!apiData) return defaults;

  return {
    ...defaults,
    tipoInviante: apiData.GPINV_CODICE ?? "",
    esclusaDaGp: toBool01(apiData.MEDPGMGP_SW_ESCL_GP),
    urgenti: toBool01(apiData.MEDPGMGP_URGENTI),
    primiAccessiPriorP: toBool01(apiData.MEDPGMGP_PROGRAMMABILI),
    primiAccessiPriorD: toBool01(apiData.MEDPGMGP_PROG_D),
    primiAccessiPriorAlta: toBool01(apiData.MEDPGMGP_PROG_A),
    altriAccessiPriorP: toBool01(apiData.MEDPGMGP_ORDINARIE),
    altriAccessiPriorD: toBool01(apiData.MEDPGMGP_ORD_D),
    altriAccessiPriorAlta: toBool01(apiData.MEDPGMGP_AREA_RISCHIO),
    farmacista: toBool01(apiData.MEDPGMGP_PREN_FARM),
    cittadino: toBool01(apiData.MEDPGMGP_PREN_CITT),
    altroOperatore: toBool01(apiData.MEDPGMGP_PREN_ALTRO),
    ccr: toBool01(apiData.MEDPGMGP_PREN_CCR),
    mmg: toBool01(apiData.MEDPGMGP_PREN_MEDICO),
    creg: toBool01(apiData.MEDPGMGP_PREN_CREG),
    espostaAlSiss: toBool01(apiData.MEDPGMGP_ESPOS_SISS),
    tipoAccessoNonSiss: apiData.MEDPGMGP_TPACC_NON_SISS ?? "",
  };
};

type CRUD = "c" | "u" | undefined;

//I valori true diventano "1", i false diventano "0". L'api accetta solo questi valori,
// quindi questa funzione è comoda per convertire i booleani del form in stringhe da inviare all'api
const fromBoolTo01 = (value?: boolean) => (value ? "1" : "0");

//Funzione per ottenere i valori di default per i vincoli prenotabilita da inserire nel form
const getDefaultValues = (selectedRow?: any): VincoliPrenotabilitaForm => ({
  programma: selectedRow?.programmaAbbinato ?? "",
  specialita: selectedRow?.specialita ?? "",
  prestazione: selectedRow?.descrizione ?? "",
  tipologia: selectedRow?.tipologia ?? "",
  tipoInviante: "",
  esclusaDaGp: false,
  urgenti: false,
  primiAccessiPriorP: false,
  primiAccessiPriorD: false,
  primiAccessiPriorAlta: false,
  altriAccessiPriorP: false,
  altriAccessiPriorD: false,
  altriAccessiPriorAlta: false,
  farmacista: false,
  cittadino: false,
  altroOperatore: false,
  ccr: false,
  mmg: false,
  creg: false,
  espostaAlSiss: false,
  tipoAccessoNonSiss: "",
});

const VincoliPrenotabilitaModal = ({ open, onClose, selectedRow }: Props) => {
  const methods = useForm<VincoliPrenotabilitaForm>({
    defaultValues: getDefaultValues(selectedRow),
  });

  const { control, reset, handleSubmit, watch } = methods;

  const [tipoInvianteRows, setTipoInvianteRows] = useState<any>([]);

  const [crudOperation, setCrudOperation] = useState<CRUD>(undefined);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] =
    useState<VincoliPrenotabilitaForm | null>(null);

  const [loading, setLoading] = useState<boolean>(false);

  //Funzione per ottenere l'elenco dei tipi Invianti
  const getTipoInvianteData = async () => {
    setLoading(true);
    try {
      const response = await generalResearch({
        tabella: "GPINV",
        codice: "",
        filtro: "",
        applicazione: "",
        attivi: "0",
      });

      setTipoInvianteRows(response?.data?.dati);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  //Funzione per recuperare i vincoli di prenotabilità se esistono
  const getMedPgmgpData = async () => {
    setLoading(true);
    try {
      const response = await medpgmgp({
        crud: "r",
        medpgmp_prgint: selectedRow?.idMedpgmp ?? "",
      });

      if (response?.data?.esito === "KO") {
        setCrudOperation("c");
        reset(getDefaultValues(selectedRow));
        return;
      }

      const apiRecord = response?.data?.dati?.medpgmgp;

      setCrudOperation("u");

      reset(mapMedpgmgpToForm(apiRecord, selectedRow));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  //All'apertura del componente recupero i vincoli di prenotabilità (se ci sono) e i tipi invianti, poi aggiorno il form
  useEffect(() => {
    if (!open) return;

    getMedPgmgpData();

    getTipoInvianteData();

    reset(getDefaultValues(selectedRow));
  }, [open, selectedRow, reset]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const espostaAlSiss = watch("espostaAlSiss");

  const openConfirmAlert = (values: VincoliPrenotabilitaForm) => {
    setPendingSubmit(values);
    setSeverity("warning");
    setMessage("Vuoi confermare il salvataggio dei vincoli di prenotabilità?");
    setShowAllert(true);
  };

  //Funzione per salvare i vincoli di prenotabilità
  //Se non sono presenti vincoli di prenotabilità (recuperati con la funzione getMedPgmgpData),
  //allora inserisco un nuovo record
  const onSubmit = async () => {
    if (!pendingSubmit) return;

    const payload = {
      MEDPGMGP_PREN_CUP: "0",
      MEDPGMGP_ESPOS_SISS: fromBoolTo01(pendingSubmit.espostaAlSiss),
      MEDPGMGP_PREN_SISS: fromBoolTo01(pendingSubmit.espostaAlSiss),
      MEDPGMGP_PREN_ALTRO: fromBoolTo01(pendingSubmit.altroOperatore),
      MEDPGMGP_PREN_MEDICO: fromBoolTo01(pendingSubmit.mmg),
      MEDPGMGP_PREN_FARM: fromBoolTo01(pendingSubmit.farmacista),
      MEDPGMGP_PREN_CCR: fromBoolTo01(pendingSubmit.ccr),
      MEDPGMGP_PREN_CITT: fromBoolTo01(pendingSubmit.cittadino),
      MEDPGMGP_PREN_CREG: fromBoolTo01(pendingSubmit.creg),
      MEDPGMGP_URGENTI: fromBoolTo01(pendingSubmit.urgenti),
      MEDPGMGP_ORDINARIE: fromBoolTo01(pendingSubmit.altriAccessiPriorP),
      MEDPGMGP_AREA_RISCHIO: fromBoolTo01(pendingSubmit.altriAccessiPriorAlta),
      MEDPGMGP_PROGRAMMABILI: fromBoolTo01(pendingSubmit.primiAccessiPriorP),
      MEDPGMGP_PROG_A: fromBoolTo01(pendingSubmit.primiAccessiPriorAlta),
      MEDPGMGP_PROG_D: fromBoolTo01(pendingSubmit.primiAccessiPriorD),
      MEDPGMGP_ORD_D: fromBoolTo01(pendingSubmit.altriAccessiPriorD),
      GPINV_CODICE: pendingSubmit.tipoInviante ?? "",
      MEDPGMGP_TPACC_NON_SISS: pendingSubmit.espostaAlSiss
        ? (pendingSubmit.tipoAccessoNonSiss ?? "")
        : "",
      MEDPGMGP_PID_ACU: "0",
      MEDPGMGP_SW_ESCL_GP: fromBoolTo01(pendingSubmit.esclusaDaGp),
    };

    try {
      setLoading(true);

      const response = await medpgmgp({
        crud: crudOperation,
        medpgmp_prgint: selectedRow?.idMedpgmp ?? "",
        dati: {
          medpgmgp: payload,
        },
      });

      if (response?.data?.esito === "KO") {
        setSeverity("error");
        setMessage(
          response?.data?.messaggio ?? "Errore durante il salvataggio.",
        );
        setShowAllert(true);
        return;
      }

      setSeverity("success");
      setMessage(
        response?.data?.messaggio ??
          "Vincoli di prenotabilità salvati correttamente.",
      );
      setShowAllert(true);
      setPendingSubmit(null);

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      console.error(error);
      setSeverity("error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={onSubmit}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}

      <Modal
        open={open}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          if (reason === "escapeKeyDown") return;
          onClose();
        }}
      >
        <Box
          sx={{
            width: "95vw",
            maxWidth: 1120,
            bgcolor: "#FFFFFF",
            borderRadius: "4px",
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            px: 3,
            pt: 2,
            pb: 3,
            maxHeight: "90dvh",
            overflowY: "auto",
          }}
        >
          <Tabs
            value={0}
            sx={{
              mb: 2,
              "& .MuiTabs-indicator": {
                backgroundColor: "#0E5AA7",
                height: 2,
              },
            }}
          >
            <Tab
              disableRipple
              label="Dettaglio Vincoli di prenotabilità"
              sx={{
                px: 2,
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 700,
                fontSize: 14,
                color: "#0E3E75 !important",
                alignItems: "flex-start",
              }}
            />
          </Tabs>

          <Grid container spacing={2.5} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="programma"
                control={control}
                label="Programma"
                placeholder="Placeholder"
                fullWidth
                disabled
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFTextField
                name="specialita"
                control={control}
                label="Specialità"
                placeholder="Placeholder"
                fullWidth
                disabled
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFTextField
                name="prestazione"
                control={control}
                label="Prestazione"
                placeholder="Placeholder"
                fullWidth
                disabled
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFTextField
                name="tipologia"
                control={control}
                label="Tipologia"
                placeholder="Placeholder"
                fullWidth
                disabled
                size="small"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                name="tipoInviante"
                control={control}
                fullWidth
                label="Tipo Inviante"
                size="small"
                options={tipoInvianteRows ?? []}
                getOptionLabel={(option: any) => option?.descrizione ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.codice) === String(value)) ??
                  null
                }
                mapOut={(option: any) => option?.codice ?? ""}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <RHFCheckbox
                name="esclusaDaGp"
                control={control}
                label="Esclusa da GP"
              />
            </Grid>
          </Grid>

          <Divider sx={{ mb: 1 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2.5,
              mb: 2,
            }}
          >
            <Box sx={sectionTitleSx}>Prenotabilità</Box>

            <RHFCheckbox name="urgenti" control={control} label="Urgenti" />
          </Box>

          {/* Per non riscrivere tante volte la stessa checkbox con solo il nome diverso dalle altre, eseguo un map su un array di tuple  */}
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {[
              ["primiAccessiPriorP", "Primi Accessi - prior. P"],
              ["primiAccessiPriorD", "Primi Accessi - prior. D"],
              ["primiAccessiPriorAlta", "Primi Accessi - prior. alta"],
              ["altriAccessiPriorP", "Altri Accessi - prior. P"],
              ["altriAccessiPriorD", "Altri Accessi - prior. D"],
              ["altriAccessiPriorAlta", "Altri Accessi - prior. alta"],
            ].map(([name, label]) => (
              <Grid item xs={12} md={4} key={name}>
                <RHFCheckbox
                  name={name as keyof VincoliPrenotabilitaForm}
                  control={control}
                  label={label}
                />
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2.5,
              mb: 2,
            }}
          >
            <Box sx={sectionTitleSx}>Operatori</Box>

            {[
              ["farmacista", "Farmacista"],
              ["cittadino", "Cittadino"],
              ["altroOperatore", "Altro operatore"],
              ["ccr", "CCR"],
              ["mmg", "MMG"],
              ["creg", "CreG"],
            ].map(([name, label]) => (
              <RHFCheckbox
                key={name}
                name={name as keyof VincoliPrenotabilitaForm}
                control={control}
                label={label}
              />
            ))}
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid
            container
            spacing={2.5}
            sx={{
              mb: 2,
              alignItems: "center",
            }}
          >
            <Grid
              item
              xs={12}
              md={3}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <RHFCheckbox
                name="espostaAlSiss"
                control={control}
                label="Esposta al SISS"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                name="tipoAccessoNonSiss"
                control={control}
                fullWidth
                label="Tipo accesso non SISS"
                disabled={!espostaAlSiss}
                size="small"
                getOptionLabel={(option: any) => option?.label ?? ""}
                mapIn={(value, opts) =>
                  opts.find((o: any) => String(o.value) === String(value)) ??
                  null
                }
                mapOut={(option: any) => option?.value ?? ""}
                options={labelNonSiss ?? []}
                rules={{
                  required: espostaAlSiss
                    ? "Inserire un tipo accesso non SISS o disabilitare il campo Esposta al SISS"
                    : false,
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}></Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{
                minWidth: 92,
                height: 36,
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 600,
                borderColor: "#0E5AA7",
                color: "#0E5AA7",
              }}
            >
              Indietro
            </Button>

            <Button
              type="button"
              variant="contained"
              onClick={handleSubmit(openConfirmAlert)}
              sx={{
                minWidth: 110,
                height: 36,
                textTransform: "none",
                fontFamily: "Montserrat",
                fontWeight: 600,
                backgroundColor: "#2F80C9",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#276EAE",
                  boxShadow: "none",
                },
              }}
            >
              Conferma
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default VincoliPrenotabilitaModal;
